import { XMarkIcon } from "@heroicons/react/24/solid";
import { Box, Grid, useMediaQuery } from "@mui/material";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import resumeUpload from "../../../../assets/lotties/job-list/resume-uploading.json";
import { convertBase64, setItem } from "../../../../helpers/data_management";
import { MAX_DROP_RESUME_BYTES } from "../../../../helpers/homepage_drop_resume";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import { uploadFileToS3 } from "../../../../helpers/update_s3";
import {
  updateHomepageReplaceResumeDialog,
  updateHomepageUploadedResumeDialog,
} from "../../../../redux/actions/dialog_action";
import { updateSignUpOrigin } from "../../../../redux/actions/navbar_action";
import {
  checkAuthentication,
  getUser,
  newResumeUpload,
} from "../../../../redux/actions/user_action";
import * as types from "../../../../redux/types/user_type";
import ClientLottie from "../../../shared/ClientLottie";
import {
  CreateResumeButton,
  CTAContainer,
  ErrorIcon,
  ErrorMessage,
  ErrorMessageContainer,
  Filename,
  FileTypeText,
  Progress,
  ResumeContainer,
  StyledContent,
  StyledContentContainer,
  StyledDocumentIcon,
  StyledLI,
  StyledLinearProgress,
  StyledTopTitle,
  StyledUL,
  TextInstruction,
  ToastContentContainer,
  ToastContentText,
  ToastErrorIcon,
  ToastTitle,
  ToastTitleContainer,
  TopSectionContainer,
  UploadButton,
  UploadingResumeContainer,
  UploadingResumeContent,
  UploadingTextContainer,
} from "./styles";

/** Same stepped progress as legacy: +5 every 100ms until progress > 100 (~2.1s). Runs in parallel with S3 via Promise.all. */
function uploadFileWithProgress(setProgress) {
  return new Promise((resolve) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      setProgress(progress);

      if (progress > 100) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
}

export default function HomepageDropResume(props) {
  const {
    triggerToast,
    setUserResume,
    inputFileRef,
    isFloatingModal = false,
    onCloseModal,
    titleKey = "homepage.drop_resume_title",
    descriptionKey = "homepage.drop_resume_description",
    onFlowComplete,
    renderContent,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const router = useRouter();

  const screenDesktop = useMediaQuery("(min-width:1280px)");
  const screenTablet = useMediaQuery("(min-width:768px)");

  const loggedIn = checkAuthentication();

  const [resumeErrorKey, setResumeErrorKey] = useState(null);
  const [filename, setFilename] = useState("");
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dragFile, setDragFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const user = getUser();
  const reduxSignUpOrigin = useSelector((state) => state.navbar.signUpOrigin);

  const getFloatingModalOrigin = () => {
    const currentPath = router.asPath?.split("?")[0]?.split("#")[0] || "";
    const normalizedPath = currentPath.replace(/^\/+|\/+$/g, "");
    const pathSegments = normalizedPath.split("/").filter(Boolean);

    if (!normalizedPath) {
      return "homepage";
    }

    if (pathSegments[0] === "companies" && pathSegments[1]) {
      return "company_details_page";
    }

    if (pathSegments[0] === "jobs" && pathSegments[1]) {
      return "job_detail_page";
    }

    if (pathSegments[0] === "connors-collective") {
      return "paid_partnership_page";
    }

    return `${normalizedPath.replace(/-/g, "_")}_page`;
  };

  const trackingOrigin =
    isFloatingModal &&
    (reduxSignUpOrigin === "jwc_redirect" ||
      reduxSignUpOrigin === "jwc_floating_resume")
      ? "job_worth_calculator"
      : isFloatingModal
      ? getFloatingModalOrigin()
      : "drop_resume_homepage";

  const trackingClickSource =
    trackingOrigin === "jwc_redirect"
      ? "form_button"
      : isFloatingModal
      ? "floating_button"
      : undefined;

  function resumeValidation(file) {
    const closeFloatingModalForStackedDialog = () => {
      if (isFloatingModal) {
        onCloseModal?.();
      }
    };

    if (loggedIn) {
      if (!isEmpty(user?.resume)) {
        sendTrackingEvent({
          event: "CE_resume_upload_reupload",
          origin: trackingOrigin,
          first_time: false,
          is_visitor: false,
          ...(trackingClickSource && { click_source: trackingClickSource }),
        });
        dispatch(updateHomepageReplaceResumeDialog(true));
        closeFloatingModalForStackedDialog();
      } else {
        sendTrackingEvent({
          event: "CE_resume_upload_first_time",
          origin: trackingOrigin,
          first_time: true,
          is_visitor: !loggedIn,
          ...(trackingClickSource && { click_source: trackingClickSource }),
        });
        handleSubmitResume(file);
      }
    } else {
      sendTrackingEvent({
        event: "CE_resume_upload_first_time",
        origin: trackingOrigin,
        first_time: true,
        is_visitor: !loggedIn,
        ...(trackingClickSource && { click_source: trackingClickSource }),
      });
      dispatch(updateHomepageUploadedResumeDialog(true));
      closeFloatingModalForStackedDialog();
    }
  }

  function handleUploadButton() {
    const renderTimeout = setTimeout(() => {
      if (inputFileRef.current) {
        inputFileRef.current.click();
      }
    }, 250);

    return () => clearTimeout(renderTimeout);
  }

  async function handleFileChange(event) {
    event?.preventDefault();
    const file = event?.target?.files ? event?.target?.files[0] : dragFile;

    if (file) {
      if (file.size > MAX_DROP_RESUME_BYTES) {
        triggerToast(
          false,
          <Grid>
            <ToastTitleContainer>
              <ToastErrorIcon />
              <ToastTitle>
                {t("homepage.drop_resume_upload_failure_title")}
              </ToastTitle>
            </ToastTitleContainer>
            <ToastContentContainer>
              <ToastContentText>
                {t("homepage.drop_resume_upload_failure_ensure")}
              </ToastContentText>
              <StyledUL>
                <StyledLI>
                  <ToastContentText>
                    {t("homepage.drop_resume_upload_failure_size")}
                  </ToastContentText>
                </StyledLI>
                <StyledLI>
                  <ToastContentText>
                    {t("homepage.drop_resume_upload_failure_format")}
                  </ToastContentText>
                </StyledLI>
              </StyledUL>
            </ToastContentContainer>
          </Grid>
        );
        setResumeErrorKey("drop_resume_file_too_large");
        return;
      }

      setFilename(file?.name);
      setLoading(true);
      setProgress(0);
      try {
        const uploadPromise = (async () => {
          let s3Key;
          try {
            s3Key = await uploadFileToS3(file);
          } catch {
            // S3 unavailable or misconfigured: continue with file only (GraphQL still gets base64).
          }
          return s3Key;
        })();

        const [, s3Key] = await Promise.all([
          uploadFileWithProgress(setProgress),
          uploadPromise,
        ]);

        setResumeErrorKey(null);
        setUserResume(s3Key ? { file, s3Key } : { file });
        resumeValidation(file);
        if (!isFloatingModal) {
          dispatch(updateSignUpOrigin("homepage-drop-resume"));
        }
      } catch {
        setResumeErrorKey("drop_resume_upload_failed_inline");
        setFilename("");
        triggerToast(false);
      } finally {
        setTimeout(() => {
          setProgress(0);
          setLoading(false);
        }, 250);
      }
    }
  }

  async function handleSubmitResume(userResume) {
    if (userResume) {
      convertBase64(userResume).then((cvFile) => {
        // Trigger graphQL to upload cvFile
        let params = {
          resume: cvFile,
          resume_drop: true,
          canCancel: false,
          user_resume: "",
          origin: isFloatingModal
            ? reduxSignUpOrigin || "drop_resume_homepage"
            : "drop_resume_homepage",
          first_time_upload_with_resume: true, // To determine if the user has already uploaded resume on onboarding/profile page and decide to submit resume via drop resume page
          autoGenerated: false,
          is_visitor: !loggedIn,
        };

        dispatch(newResumeUpload(params)).then((response) => {
          if (response.type === types.UPDATE_USER_CV_SUCCEED) {
            setItem("HOMEPAGE-RESUME-UPLOADED", true);
            setItem(types.USER_PROFILE, JSON.stringify(response.user));

            if (onFlowComplete) {
              onFlowComplete(response);
              return;
            }

            router.push("/jobs");
          } else {
            // toast(
            //   response.payload?.message
            //     ? response.payload.message
            //     : "Failed to update resume, please try again later"
            // );
            return;
          }
        });
      });
    }
  }

  function handleDragFile(event) {
    event.preventDefault();
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    if (dragActive) {
      setDragActive(false);
    }

    if (e.dataTransfer?.files?.[0]) {
      const file = e.dataTransfer.files[0];
      handleFileChange({ preventDefault: () => {}, target: { files: [file] } });
    }
  }

  if (renderContent) {
    return renderContent({
      handleFileChange,
      handleUploadButton,
    });
  }

  return (
    <Box
      sx={
        isFloatingModal
          ? {
              width: "100%",
              maxWidth: "560px",
              mx: "auto",
              p: { xs: 2, md: "16px 24px" },
              background: "#FFFFFF",
              boxShadow: "2px 4px 10px rgba(0, 0, 0, 0.12)",
              borderRadius: "16px",
              outline: "1.5px solid #222222",
              outlineOffset: "-1.5px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }
          : {
              display: "flex",
              flexDirection: "column",
              alignItem: "center",
              borderRadius: "8px",
              border: "1px dashed #512ACC",

              width: "100%",
              height: "100%",
              maxHeight: "500px",

              "@media(max-width: 767.9px)": {
                minHeight: "fit-content",
                maxHeight: "180px",
              },
            }
      }
    >
      <ResumeContainer
        sx={
          isFloatingModal
            ? {
                border: "none",
                borderRadius: 0,
                maxHeight: "none",
                height: "auto",
              }
            : {}
        }
      >
        {isFloatingModal ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
            >
              <Box
                sx={{
                  color: "#222222",
                  fontSize: "20px",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 700,
                  lineHeight: "24px",
                }}
              >
                {t(titleKey)}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onCloseModal}
                aria-label="Close upload modal"
                sx={{
                  width: 24,
                  height: 24,
                  minWidth: 24,
                  border: "none",
                  background: "transparent",
                  color: "#222222",
                  fontSize: "20px",
                  lineHeight: "20px",
                  cursor: "pointer",
                  p: 0,
                }}
              >
                <XMarkIcon height="24px" width="24px" />
              </Box>
            </Box>
            <Box
              sx={{
                color: "#222222",
                fontSize: "16px",
                fontFamily: "Inter, sans-serif",
                fontWeight: 400,
                lineHeight: "24px",
                letterSpacing: "0.15px",
              }}
            >
              {t(descriptionKey)}
            </Box>
          </Box>
        ) : (
          <Grid>
            <TopSectionContainer>
              <StyledTopTitle>{t(titleKey)}</StyledTopTitle>
            </TopSectionContainer>
          </Grid>
        )}
      </ResumeContainer>
      <Grid
        sx={{
          background: "#F5F5F5",
          height: isFloatingModal ? "auto" : "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: isFloatingModal ? "8px" : "0 0 8px 8px",
          padding: isFloatingModal ? "20px 8px" : "8px 0",
          outline: isFloatingModal
            ? "1px dotted rgba(64, 36, 155, 0.50)"
            : "none",
          outlineOffset: isFloatingModal ? "-1px" : 0,
          maxHeight: "222px",
        }}
        onDragOver={handleDragFile}
        onDrop={handleDrop}
      >
        <StyledContentContainer>
          <ClientLottie
            options={{
              loop: true,
              autoplay: true,
              animationData: resumeUpload,
            }}
            width={screenTablet ? "100px" : "64px"}
            height={screenTablet ? "100px" : "64px"}
          />
          <StyledContent error={!!resumeErrorKey}>
            {resumeErrorKey && (
              <ErrorMessageContainer>
                <ErrorIcon />
                <ErrorMessage>{t(`homepage.${resumeErrorKey}`)}</ErrorMessage>
              </ErrorMessageContainer>
            )}
            <Grid
              display={"flex"}
              flexDirection={"column"}
              gap={screenTablet ? "6px" : "4px"}
            >
              {loading ? (
                <UploadingResumeContainer>
                  <Grid>
                    <StyledDocumentIcon />
                  </Grid>
                  <UploadingResumeContent>
                    <UploadingTextContainer>
                      <Filename>{filename}</Filename>
                      <Progress>{progress}%</Progress>
                    </UploadingTextContainer>
                    <StyledLinearProgress
                      variant="determinate"
                      value={Math.min(progress, 100)}
                    />
                  </UploadingResumeContent>
                </UploadingResumeContainer>
              ) : (
                <CTAContainer>
                  {screenDesktop && !resumeErrorKey && (
                    <TextInstruction
                      sx={{ fontWeight: 600, textAlign: "center" }}
                    >
                      {t("homepage.drop_resume_drag_drop")}{" "}
                      {t("homepage.drop_resume_or")}
                    </TextInstruction>
                  )}

                  <Grid display={"flex"} gap={"6px"} flexDirection={"row"}>
                    <UploadButton
                      onClick={handleUploadButton}
                      data-testid="homepage-drop-resume-upload-btn"
                    >
                      {t("homepage.drop_resume_upload_button")}
                    </UploadButton>

                    <CreateResumeButton
                      onClick={() =>
                        window.open("https://www.hiresume.my", "_blank")
                      }
                      data-testid="homepage-drop-resume-create-resume-btn"
                    >
                      {t("homepage.drop_resume_create_resume")}
                    </CreateResumeButton>
                  </Grid>
                  <input
                    style={{ display: "none" }}
                    ref={inputFileRef}
                    accept=".pdf,.docx,.doc"
                    type="file"
                    onChange={handleFileChange}
                    data-testid="homepage-drop-resume-input"
                  />
                </CTAContainer>
              )}
              <FileTypeText>
                {t("homepage.drop_resume_file_types")}
              </FileTypeText>
            </Grid>
          </StyledContent>
        </StyledContentContainer>
      </Grid>
    </Box>
  );
}
